import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BROKEN_IMAGE_ERROR } from '../../config/app';
import { LANGUAGE, LOCALE } from '../../config/locale';
import 'external-svg-loader';
import './style.css';

/**
 * Renders an image that displays a skeleton while loading, or an error message if broken
 * @param {*} props Props passed to this component
 * @returns {React.ReactComponentElement} Preload Image Component
 */
const PreloadImage = props => {
	const classes = ['adi-isp__preload-image'];
	const { alt, className, height, src, width } = props;
	const svgRef = useRef(null);
	const isSvg = src.includes('.svg');
	const [isImageLoading, setIsImageLoading] = useState(true);
	const [isImageBroken, setIsImageBroken] = useState(false);

	/**
	 * Localize svg image anchors according to the current page locale
	 */
	const localizeSvgAnchors = () => {
		const svgAnchors = svgRef.current.querySelectorAll('a') ?? [];
		const partHavingCurrentLocale = `analog.com/${LOCALE}/`;

		svgAnchors.forEach(anchor => {
			const href = anchor.getAttribute('href');
			const doesHrefHaveLocale = /(\/)(en|jp|cn)(\/)/i.test(href);
			const partToBeReplaced = doesHrefHaveLocale
				? /analog\.com(\/)(en|jp|cn)(\/)/i
				: /analog\.com(\/)/i;

			anchor.setAttribute('href', href.replace(partToBeReplaced, partHavingCurrentLocale));
		});
	};

	/**
	 * Takes care of image/svg initialization
	 */
	useEffect(() => {
		setIsImageLoading(true);
		setIsImageBroken(false);

		const sensorImage = new Image();
		sensorImage.src = src;

		sensorImage.onload = () => setIsImageLoading(false);
		sensorImage.onerror = () => {
			console.warn(`Failed to load "${alt}" of src: "${src}"`);
			setIsImageLoading(false);
			setIsImageBroken(true);
		};
	}, [alt, src]);

	/**
	 * Localizes the anchors of the most recently loaded svg
	 */
	useEffect(() => {
		const svg = svgRef.current;
		if (isSvg) svg.addEventListener('iconload', localizeSvgAnchors);

		return () => {
			if (isSvg) svg.removeEventListener('iconload', localizeSvgAnchors);
		};
	}, [isSvg]);

	/**
	 * Modifiers
	 */
	if (isImageLoading) classes.push(`${classes[0]}--loading`);
	if (className) classes.push(className);

	return isImageBroken ? (
		<p className={`${classes[0]}--broken`}>
			<i className="fas fa-exclamation-triangle fa-2x" />
			{BROKEN_IMAGE_ERROR[LANGUAGE]}
		</p>
	) : (
		<figure className={classes.join(' ')}>
			{isSvg ? (
				<svg data-src={src} alt={alt} ref={svgRef} />
			) : (
				<img src={src} alt={alt} width={width} height={height} loading="lazy" />
			)}
		</figure>
	);
};

PreloadImage.defaultProps = {
	className: '',
	height: 'auto',
	width: 'auto',
};

PreloadImage.propTypes = {
	alt: PropTypes.string.isRequired,
	className: PropTypes.string,
	height: PropTypes.string,
	src: PropTypes.string.isRequired,
	width: PropTypes.string,
};

export default PreloadImage;
